import _extends from "@babel/runtime/helpers/extends";
import { ClassNames } from "@emotion/core";
import { ursaColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { getClientId } from "@smartsheet/lodestar-utils";
import { LoggerContext, LogActionId } from "@smartsheet/logging-state-service";
import React, { useContext, useMemo, useState } from "react";
import { useTheme } from "../../theme/utils/useTheme";
import { FOCUS_USING_BEFORE_CLASS_NAME } from "../../util/focusRing";
import { LoggingDecorator } from "../../util/logging";
import { BaseButtonStyled } from "./styled/BaseButtonStyled";
import { ButtonBadge, getBadgeSize } from "./subComponents/ButtonBadge";
import { Content } from "./subComponents/Content";
import { IconWrapper } from "./subComponents/IconWrapper";
import { LoadingSpinner } from "./subComponents/LoadingSpinner";
const getBtnApperance = (appearance, themeMode) => {
  //https://app.smartsheet.com/sheets/J26gVQgGrGQ64wq5q9gRRMr2QcCGV5J8CMVhrgV1?rowId=4147589427138436 lodestar-light themed Buttons should map the canvas appeareances to secondary
  if (themeMode === "lodestar-light") {
    return {
      btnAppearance: appearance.includes("canvas") ? "secondary" : appearance,
      isCanvasEmptyAndLodestar: appearance === "canvas-empty" ? true : false
    };
  }
  return {
    btnAppearance: appearance,
    isCanvasEmptyAndLodestar: false
  };
};
export const BaseButton = _ref => {
  let {
    children,
    className,
    clientId,
    focusMode,
    iconAfter,
    iconBefore,
    innerRef,
    isDisabled = false,
    isLoading = false,
    renderAs = "button",
    shouldFitContainer = false,
    size = "large",
    tabIndex,
    badgeProps,
    appearance = renderAs === "button" ? "primary" : "link",
    ...rest
  } = _ref;
  const logger = useContext(LoggerContext);
  const {
    key
  } = useTheme();
  const themeMode = key;
  const {
    btnAppearance,
    isCanvasEmptyAndLodestar
  } = getBtnApperance(appearance, themeMode);
  const withLogging = new LoggingDecorator(logger, {
    clientId,
    controlType: "btn"
  });
  const [isActive, setIsActive] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isInteractive = !isDisabled && !isLoading;
  const onMouseEnter = e => {
    setIsHovered(true);
    if (rest.onMouseEnter) {
      rest.onMouseEnter(e);
    }
  };
  const onMouseLeave = e => {
    setIsActive(false);
    setIsHovered(false);
    if (rest.onMouseLeave) {
      rest.onMouseLeave(e);
    }
  };
  const onMouseDown = e => {
    setIsActive(true);
    if (rest.onMouseDown) {
      void withLogging.disabledByDefault({
        actionId: LogActionId.MOUSE_DOWN
      }, rest.onMouseDown)(e);
    }
  };
  const onMouseUp = e => {
    setIsActive(false);
    if (rest.onMouseUp) {
      void withLogging.disabledByDefault({
        actionId: LogActionId.MOUSE_UP
      }, rest.onMouseUp)(e);
    }
  };
  const onClick = e => {
    if (isInteractive) {
      if (rest.onClick) {
        void withLogging.enabledByDefault({
          actionId: LogActionId.CLICK
        }, rest.onClick)(e);
      }
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const onDoubleClick = e => {
    if (isInteractive) {
      if (rest.onDoubleClick) {
        void withLogging.enabledByDefault({
          actionId: LogActionId.DOUBLE_CLICK
        }, rest.onDoubleClick)(e);
      }
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const onFocus = event => {
    setIsFocused(true);
    if (rest.onFocus) {
      rest.onFocus(event);
    }
  };
  const onBlur = event => {
    setIsFocused(false);
    if (rest.onBlur) {
      rest.onBlur(event);
    }
  };
  const hasLeftIcon = iconBefore !== undefined;
  const hasRightIcon = iconAfter !== undefined;
  const iconRenderState = {
    isActive,
    isFocus: isFocused,
    isHover: isHovered
  };
  const styleProps = {
    state: {
      ...iconRenderState,
      isDisabled
    },
    shouldFitContainer,
    size,
    focusMode,
    isLoading,
    appearance: btnAppearance.replace("button-", ""),
    hasOnlyIcons: children == null && (hasLeftIcon || hasRightIcon),
    hasNoIcons: !hasLeftIcon && !hasRightIcon,
    renderAs,
    themeMode,
    isCanvasEmptyAndLodestar
  };
  const RootComponent = useMemo(() => {
    return renderAs === "a" ? BaseButtonStyled.withComponent("a") : BaseButtonStyled;
  }, [renderAs]);

  // TODO: swap out custom component instead of merging it with our css
  return /*#__PURE__*/React.createElement(ClassNames, null, _ref2 => {
    let {
      cx
    } = _ref2;
    return /*#__PURE__*/React.createElement(RootComponent, _extends({}, rest, {
      "data-client-id": clientId,
      ref: innerRef,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onMouseDown: onMouseDown,
      onMouseUp: onMouseUp,
      onFocus: onFocus,
      onBlur: onBlur,
      "aria-disabled": isDisabled,
      className: cx(className, FOCUS_USING_BEFORE_CLASS_NAME),
      onClick: onClick,
      onDoubleClick: onDoubleClick,
      tabIndex: tabIndex
    }, styleProps), isLoading && /*#__PURE__*/React.createElement(LoadingSpinner, {
      appearance: btnAppearance,
      themeMode: themeMode
    }), iconBefore && /*#__PURE__*/React.createElement(IconWrapper, {
      hasLeftIcon: hasLeftIcon,
      hasRightIcon: hasRightIcon,
      hasOnlyIcons: styleProps.hasOnlyIcons,
      iconLocation: "before",
      isLoading: isLoading,
      appearance: btnAppearance,
      size: size,
      themeMode: themeMode
    }, typeof iconBefore === "function" ? iconBefore(iconRenderState) : iconBefore), children && /*#__PURE__*/React.createElement(Content, {
      size: size,
      isLoading: isLoading,
      appearance: btnAppearance,
      hasLeftIcon: hasLeftIcon,
      hasRightIcon: hasRightIcon,
      themeMode: themeMode
    }, children), iconAfter && /*#__PURE__*/React.createElement(IconWrapper, {
      hasLeftIcon: hasLeftIcon,
      hasRightIcon: hasRightIcon,
      iconLocation: "after",
      hasOnlyIcons: styleProps.hasOnlyIcons,
      isLoading: isLoading,
      appearance: btnAppearance,
      size: size,
      themeMode: themeMode
    }, typeof iconAfter === "function" ? iconAfter(iconRenderState) : iconAfter), badgeProps && /*#__PURE__*/React.createElement(ButtonBadge, _extends({
      "data-client-id": getClientId(clientId, "badge"),
      size: getBadgeSize(size),
      backgroundColor: isDisabled || badgeProps.emphasis === "weak" ? color("color--background--notifications--weak", ursaColors.slate300) : color("color--background--notifications--default", ursaColors.orange500),
      buttonSize: size,
      color: color("color--text--neutral-inverse--strong", ursaColors.neutral00)
    }, badgeProps), !size.includes("small") && badgeProps.number !== null && (badgeProps.number && badgeProps.number > 99 ? "99+" : badgeProps.number)));
  });
};
//# sourceMappingURL=BaseButton.js.map